import React, { useState, useEffect } from 'react';
import {
  FormGroup,
  InputGroup,
  Button,
  Callout,
  Spinner,
  Icon,
} from '@blueprintjs/core';

const defaultFormState = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
};

const ProfileSettingsForm = ({
  initialValues,
  status,
  error,
  success,
  onSubmit,
}) => {
  const [formValues, setFormValues] = useState({
    ...defaultFormState,
    ...initialValues,
    password: '',
  });

  useEffect(() => {
    setFormValues({
      ...defaultFormState,
      ...initialValues,
      password: '',
    });
  }, [initialValues]);

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!onSubmit) {
      return;
    }

    const payload = {
      first_name: formValues.first_name?.trim(),
      last_name: formValues.last_name?.trim(),
      email: formValues.email?.trim(),
    };

    if (formValues.password) {
      payload.password = formValues.password;
    }

    try {
      await onSubmit(payload);
      setFormValues((prev) => ({
        ...prev,
        password: '',
      }));
    } catch (submitError) {
      // Errors are handled by parent component
    }
  };

  const isSaving = status === 'saving';

  return (
    <form onSubmit={handleSubmit} className="profile-settings-form">
      <div style={{ marginBottom: '24px' }}>
        <h2>
          <Icon icon="user" intent="primary" />
          Account Profile
        </h2>
        <p className="profile-settings-form__subtitle">
          Update your personal information and change your password.
        </p>
      </div>

      {error && (
        <Callout intent="danger" style={{ marginBottom: '16px' }}>
          {error}
        </Callout>
      )}

      {success && (
        <Callout intent="success" style={{ marginBottom: '16px' }}>
          {success}
        </Callout>
      )}

      <div className="profile-settings-grid">
        <FormGroup label="First Name" labelFor="settings-first-name" requiredLabel>
          <InputGroup
            id="settings-first-name"
            large
            value={formValues.first_name}
            onChange={handleChange('first_name')}
            placeholder="Enter your first name"
          />
        </FormGroup>

        <FormGroup label="Last Name" labelFor="settings-last-name" requiredLabel>
          <InputGroup
            id="settings-last-name"
            large
            value={formValues.last_name}
            onChange={handleChange('last_name')}
            placeholder="Enter your last name"
          />
        </FormGroup>

        <FormGroup label="Email" labelFor="settings-email" requiredLabel>
          <InputGroup
            id="settings-email"
            large
            type="email"
            value={formValues.email}
            onChange={handleChange('email')}
            placeholder="you@example.com"
          />
        </FormGroup>

        <FormGroup
          label="Password"
          labelFor="settings-password"
          helperText="Leave blank to keep your current password."
        >
          <InputGroup
            id="settings-password"
            large
            type="password"
            value={formValues.password}
            onChange={handleChange('password')}
            placeholder="Enter a new password"
          />
        </FormGroup>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
        <Button
          type="button"
          onClick={() =>
            setFormValues({
              ...defaultFormState,
              ...initialValues,
              password: '',
            })
          }
          disabled={isSaving}
        >
          Reset
        </Button>
        <Button
          intent="primary"
          type="submit"
          large
          loading={isSaving}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {isSaving && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
          <Spinner size={24} intent="primary" />
        </div>
      )}
    </form>
  );
};

export default ProfileSettingsForm;

